import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 font-sans font-semibold text-sm rounded-pill transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/50 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:     'bg-primary-500 text-white hover:bg-primary-600 shadow-soft hover:shadow-warm',
        destructive: 'bg-error text-white hover:bg-[#8C361F]',
        outline:     'border-2 border-primary-500 text-primary-500 hover:bg-primary-50 bg-transparent',
        secondary:   'bg-cream-3 text-ink hover:bg-cream-3/80 border border-rule',
        ghost:       'text-primary-500 hover:bg-primary-50/70 bg-transparent',
        link:        'text-primary-500 underline-offset-4 hover:underline bg-transparent',
        accent:      'bg-accent-500 text-white hover:bg-accent-600 shadow-soft',
      },
      size: {
        default: 'h-12 px-6 py-2',
        sm:      'h-9 px-4 py-1.5 text-xs',
        lg:      'h-14 px-8 py-3 text-base',
        icon:    'h-10 w-10',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={loading || props.disabled}
        {...props}
      >
        {loading ? (
          <span className="flex items-center gap-2">
            <svg className="animate-spin h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            {children}
          </span>
        ) : children}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
